import {
  Stack,
  Tabs,
  useDisclosure,
  Box,
  VStack,
  Grid,
} from "@chakra-ui/react";
import { useSchoolsContext } from "../../../../Contexts/schoolContext";
import { AssessmentType, Level } from "../../../../services/Dtos";
import {  useState } from "react";
import Loader from "../../../CustomUi/Loader";
import CustomAlert from "../../../CustomUi/CustomAlert";
import AssessmentTypeModal from "./AssessmentTypeModal";
import AssessmentTypeCard from "./AssessmentTypeCard";
import Empty from "../../../CustomUi/Empty";
import { useNavigate } from "react-router-dom";
import SectionHeader from "../../../CustomUi/SectionHeader";
import useIcons from "../../../../hooks/useIcons";
import CustomTabList from "../../../CustomUi/CustomTabList";
import Cad from "../../../CustomUi/Cad";
import CustomButton from "../../../CustomUi/CustomButton";
import { getNextOrder } from "../../../../services/Utils";

function AssessmentTypeSettings() {
  const { assessmentTypes, levels, isLoading, error, accentColor } =
    useSchoolsContext();

  const [assessmentTypeToEdit, setAssessmentTypeToEdit] =
    useState<AssessmentType | null>(null);
  const [assessmentLevel, setAssessmentLevel] = useState<Level | null>(null);
  const { onOpen, open, onClose } = useDisclosure();
  const [nextOrder, setNextOrder] = useState(0);
  const icons = useIcons();
  const navigate = useNavigate();

  const handleRefresh = () => {
    // Refresh functionality can be implemented here
  };


  const handleAddAssessmentType = (level: Level) => {
    setAssessmentTypeToEdit(null);
    setAssessmentLevel(level);
    setNextOrder(getNextOrder(assessmentTypes.filter((a) => a.levelId === level.id)));
    onOpen();
  };

  const handleEditAssessmentType = (assessmentType: AssessmentType) => {
    setAssessmentTypeToEdit(assessmentType);
    setAssessmentLevel(
      levels.find((l) => l.id === assessmentType.levelId) || null
    );
    onOpen();
  };

  if (isLoading) {
    return <Loader />;
  }

  if (error) {
    return <CustomAlert message={error} handleClick={handleRefresh} />;
  }

  return (
    <VStack gap={8} h="100%">
      <AssessmentTypeModal
        nextOrder={nextOrder}
        isOpen={open}
        onClose={onClose}
        assessmentTypeToEdit={assessmentTypeToEdit}
        assessmentLevel={assessmentLevel}
      />
      {levels.length > 0 ? (
        <Cad minimal>
          <VStack gap={6} align="stretch">
            <SectionHeader
              title="Assessment Types"
              subtitle="Manage assessment types for all school levels"
              icon={icons.main.assessment}
            />
            {/* Tabs for Levels */}
            <Tabs.Root variant="enclosed" colorPalette={accentColor} fitted>
              <CustomTabList
                items={levels.map((level) => ({
                  id: level.id,
                  label: level.levelName,
                }))}
                colorPalette={accentColor}
              >
                {(item) => {
                  const level = levels.find((l) => l.id === item.id);
                  if (!level) return null;

                  return (
                    <VStack gap={6} align="stretch">
                      {/* Level Header */}
                      <Stack
                        direction={{ base: "column", md: "row" }}
                        align={{ base: "stretch", md: "center" }}
                        justify="flex-end"
                        w="full"
                        gap={3}
                      >
                        <CustomButton
                          icon={icons.utility.plus}
                          label="Add Assessment Type"
                          colorPalette={accentColor}
                          w={{ base: "100%", md: "auto" }}
                          onClick={() => handleAddAssessmentType(level)}
                          _hover={{
                            transform: "translateY(-1px)",
                          }}
                          transition="all 0.2s"
                        />
                      </Stack>
                      {/* Assessment Types Grid */}
                      {assessmentTypes.filter((a) => a.levelId === level.id)
                        .length > 0 ? (
                        <Grid
                          templateColumns={{
                            base: "1fr",
                            lg: "repeat(2, 1fr)",
                            xl: "repeat(3, 1fr)",
                          }}
                          gap={6}
                        >
                          {assessmentTypes
                            .filter((a) => a.levelId === level.id)
                            .map((s) => (
                              <AssessmentTypeCard
                                key={s.id}
                                assessmentType={s}
                                handleClick={() => handleEditAssessmentType(s)}
                              />
                            ))}
                        </Grid>
                      ) : (
                        <Box py={8}>
                          <Empty
                            message={`No assessment types created for ${level.levelName} yet`}
                            hasButton
                            buttonText={`Create First Assessment Type`}
                            handleClick={() => handleAddAssessmentType(level)}
                          />
                        </Box>
                      )}
                    </VStack>
                  );
                }}
              </CustomTabList>
            </Tabs.Root>
          </VStack>
        </Cad>
      ) : (
        <Cad minimal>
          <Empty
            hasButton
            message="Something will show up here when you set up School Levels"
            buttonText="Go to Levels Settings"
            handleClick={() => navigate("/settings/level")}
          />
        </Cad>
      )}
    </VStack>
  );
}

export default AssessmentTypeSettings;
